import type { SheetSpec } from '@meridian-ssa/shared';
import { useMemo, useState } from 'react';

import type { SpreadJSEngineResult } from '@/lib/engine/spreadjs-engine-client';

interface HyperFormulaEngineResult {
  errorCells: string[];
  errors: string[];
  detailedErrors: Array<{
    cell: string;
    errorType: string;
    formula?: string;
    message?: string;
  }>;
}

interface EngineDiffViewProps {
  spreadjsResult: SpreadJSEngineResult;
  hyperformulaResult: HyperFormulaEngineResult;
  sheetSpec: SheetSpec;
}

interface CellDiff {
  cell: string;
  spreadjsValue: any;
  hyperformulaValue: string;
  formula?: string;
}

/**
 * Format a value for display (handles nulls, objects, etc.)
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'object' && value.toString) return value.toString();
  return String(value);
}

export function EngineDiffView({
  spreadjsResult,
  hyperformulaResult,
  sheetSpec,
}: EngineDiffViewProps) {
  // Find cells where engines have different values or errors
  const cellDiffs = useMemo(() => {
    const diffs: CellDiff[] = [];

    // Get all cells with errors from either engine
    const allErrorCells = new Set([
      ...spreadjsResult.errorCells,
      ...hyperformulaResult.errorCells,
    ]);

    allErrorCells.forEach(cellRef => {
      const spreadjsError = spreadjsResult.detailedErrors.find(
        e => e.cell === cellRef
      );
      const hyperformulaError = hyperformulaResult.detailedErrors.find(
        e => e.cell === cellRef
      );

      // Find the formula for this cell
      const formula = sheetSpec.sheets
        .flatMap(s => s.cells)
        .find(c => {
          const [sheetName, cellA1] = cellRef.includes('!')
            ? cellRef.split('!')
            : [sheetSpec.sheets[0].name, cellRef];
          return c.a1 === cellA1 && c.type === 'formula';
        })?.formula;

      diffs.push({
        cell: cellRef,
        spreadjsValue: spreadjsError?.errorType || '(no error)',
        hyperformulaValue: hyperformulaError?.errorType || '(no error)',
        formula,
      });
    });

    return diffs;
  }, [spreadjsResult, hyperformulaResult, sheetSpec]);

  if (cellDiffs.length === 0) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#8B7764',
          fontSize: '0.875rem',
          background: 'rgba(239, 235, 229, 0.5)',
          borderRadius: '6px',
          border: '1px solid #D1C9C4',
        }}
      >
        ✓ Both engines agree - no differences found
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            border: '1px solid #D1C9C4',
          }}
        >
          <thead>
            <tr
              style={{
                background: '#EFE9E5',
                borderBottom: '2px solid #D1C9C4',
              }}
            >
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  color: '#47331F',
                  fontWeight: 600,
                }}
              >
                Cell
              </th>
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  color: '#47331F',
                  fontWeight: 600,
                }}
              >
                Formula
              </th>
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  color: '#47331F',
                  fontWeight: 600,
                }}
              >
                SpreadJS
              </th>
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  color: '#47331F',
                  fontWeight: 600,
                }}
              >
                HyperFormula
              </th>
            </tr>
          </thead>
          <tbody>
            {cellDiffs.map((diff, idx) => (
              <tr
                key={`${diff.cell}-${idx}`}
                style={{
                  borderBottom: '1px solid #E8E3DD',
                  background: 'white',
                }}
              >
                <td
                  style={{
                    padding: '0.75rem',
                    fontFamily: 'monospace',
                    color: '#47331F',
                  }}
                >
                  {diff.cell}
                </td>
                <td
                  style={{
                    padding: '0.75rem',
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    color: '#8B7764',
                  }}
                >
                  {diff.formula || '-'}
                </td>
                <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>
                  <span
                    style={{
                      color:
                        diff.spreadjsValue === '(no error)'
                          ? '#10b981'
                          : '#ef4444',
                    }}
                  >
                    {formatValue(diff.spreadjsValue)}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>
                  <span
                    style={{
                      color:
                        diff.hyperformulaValue === '(no error)'
                          ? '#10b981'
                          : '#ef4444',
                    }}
                  >
                    {formatValue(diff.hyperformulaValue)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
